import React, { useState } from 'react';

import { css, StyleSheet } from 'aphrodite';
import { Button, Col, Row, Table, Form } from 'react-bootstrap';
import Select from 'react-select';
import Swal from 'sweetalert2';
import { useDebounce } from 'use-debounce';
import { useHistory } from 'react-router-dom';

import ActionsMenu from 'components/shared/ActionsMenu';
import { backgroundGrey } from 'lib/css/colors';
import Container from 'components/shared/Container';
import CopyTextModal from 'components/shared/CopyTextModal';
import { DEFAULT_PAGE_SIZE } from 'lib/constants';
import { EntityTypeLabels } from 'lib/enums/EntityType';
import {
  GLOBAL_MANUFACTURERS_FOR_DROPDOWN,
  GLOBAL_VENDORS_FOR_DROPDOWN,
  NUTRITIONAL_PANELS,
  NUTRITIONAL_PANELS_REPROCESS,
} from 'lib/networking/endpoints';
import LoadingSpinner from 'components/shared/LoadingSpinner';
import { NutritionalPanelInputTypeLabels } from 'lib/enums/NutritionalPanelInputTypes';
import NutritionalPanelUploadModal from 'components/nutritional_panels/NutritionalPanelUploadModal';
import { OCRStatus, OCRStatusLabels } from 'lib/enums/OCRStatus';
import PaginationComponent from 'components/shared/PaginationComponent';
import ProcessStatus, { ProcessStatusLabels } from 'lib/enums/ProcessStatus';
import SearchableScrollableSelect from 'components/shared/SearchableScrollableSelect';
import useGet from 'lib/hooks/useGet';
import usePost from 'lib/hooks/usePost';

function NutritionalPanelUploadView() {
  const history = useHistory();
  const [showPanelUploadModal, setShowPanelUploadModal] = useState(false);
  const [selectedOcrKey, setSelectedOcrKey] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(DEFAULT_PAGE_SIZE);
  const [selectedManufacturer, setSelectedManufacturer] = useState(null);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [selectedOcrStatus, setSelectedOcrStatus] = useState(null);
  const [skuSearch, setSkuSearch] = useState('');
  const [debouncedSkuSearch] = useDebounce(skuSearch, 500);

  const {
    data: { nutritional_panels, count } = {},
    loading,
    refetch,
  } = useGet(NUTRITIONAL_PANELS, {
    limit: itemsPerPage,
    offset: itemsPerPage * (currentPage - 1),
    ...(selectedManufacturer && { manufacturer_id: selectedManufacturer.value }),
    ...(selectedVendor && { vendor_id: selectedVendor.value }),
    ...(selectedOcrStatus && { ocr_status: selectedOcrStatus.value }),
    ...(debouncedSkuSearch && { sku: debouncedSkuSearch }),
  });

  const { postData: reprocessPanel, loading: reprocessLoading } = usePost(
    NUTRITIONAL_PANELS_REPROCESS,
    () => {
      refetch();
      Swal.fire({
        icon: 'success',
        text: 'Nutritional panel data has been sent for reprocessing',
        title: 'Success',
      });
    },
    error =>
      Swal.fire({
        icon: 'error',
        text: 'Failed to reprocess nutritional panel. Please try again.',
        title: 'Error',
      }),
  );

  const handleReprocess = uploadId => {
    reprocessPanel({ upload_id: uploadId });
  };

  const { data: { data: globalVendors } = {} } = useGet(GLOBAL_VENDORS_FOR_DROPDOWN);

  const ocrStatusOptions = Object.keys(OCRStatus).map(key => ({
    value: key,
    label: OCRStatusLabels[key],
  }));

  if (loading) {
    return <LoadingSpinner short />;
  }

  return (
    <Container>
      <div className="mb-3 d-flex justify-content-between">
        <h3 className="font-weight-bold">Upload Nutritional Panels</h3>
        <Button
          className="mr-3 px-4"
          variant="outline-primary"
          onClick={() => setShowPanelUploadModal(true)}
        >
          Upload
        </Button>
      </div>
      <div className="mb-3">
        <Row>
          <Col md={3}>
            <Form.Group>
              <Form.Label>Select Manufacturer</Form.Label>
              <SearchableScrollableSelect
                onChange={option => {
                  setSelectedManufacturer(option);
                  setCurrentPage(1);
                }}
                fetchUrl={GLOBAL_MANUFACTURERS_FOR_DROPDOWN}
                optionMapper={manufacturer => ({
                  label: manufacturer.name,
                  value: manufacturer.id,
                })}
                initOption={selectedManufacturer}
                autoFocus={false}
                placeholder="Search manufacturer..."
                disabled={selectedVendor}
              />
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group>
              <Form.Label>Select Distributor</Form.Label>
              <Select
                value={selectedVendor}
                options={globalVendors?.map(vendor => ({
                  value: vendor.id,
                  label: vendor.name,
                }))}
                onChange={e => {
                  setSelectedVendor(e);
                  setCurrentPage(1);
                }}
                placeholder="Search distributor..."
                isDisabled={selectedManufacturer}
                isSearchable
                isClearable
              />
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group>
              <Form.Label>OCR Status</Form.Label>
              <Select
                value={selectedOcrStatus}
                options={ocrStatusOptions}
                onChange={e => {
                  setSelectedOcrStatus(e);
                  setCurrentPage(1);
                }}
                placeholder="Select OCR status..."
                isSearchable
                isClearable
              />
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group>
              <Form.Label>Search by SKU</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter SKU"
                value={skuSearch}
                onChange={e => setSkuSearch(e.target.value)}
              />
            </Form.Group>
          </Col>
        </Row>
      </div>
      <Row>
        <div className="w-100 d-flex justify-content-between px-3 py-3">
          <div>
            <p className="mb-0">{count || 0} panel uploads found</p>
          </div>
          <PaginationComponent
            totalItems={count}
            itemsPerPage={itemsPerPage}
            setItemsPerPage={setItemsPerPage}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            disabled={loading}
          />
        </div>
      </Row>
      <Table className={css(styles.tableText)}>
        <thead className={css(styles.thead)}>
          <tr>
            <th>Name</th>
            <th>Entity Type</th>
            <th>Entity Name</th>
            <th>Input Type</th>
            <th>Upload Status</th>
            <th>OCR Status</th>
            <th># Nutritional Panels</th>
            <th>Date Created</th>
            <th />
          </tr>
        </thead>
        <tbody className={css(styles.tbody)}>
          {nutritional_panels?.length ? (
            nutritional_panels.map((nutritional_panel, index) => (
              <tr key={index}>
                <td className={css(styles.nameCell)} title={nutritional_panel.name}>
                  {nutritional_panel.name}
                </td>
                <td>
                  {EntityTypeLabels[nutritional_panel.entity_type] || nutritional_panel.entity_type}
                </td>
                <td>
                  {nutritional_panel.global_manufacturer?.name ||
                    nutritional_panel.global_vendor?.name}
                </td>
                <td>
                  {NutritionalPanelInputTypeLabels[nutritional_panel.input_type] ||
                    nutritional_panel.input_type}
                </td>
                <td>{ProcessStatusLabels[nutritional_panel.status] || nutritional_panel.status}</td>
                <td>{OCRStatusLabels[nutritional_panel.ocr_status]}</td>
                <td>{nutritional_panel.panel_count}</td>
                <td>{nutritional_panel.date_created}</td>
                <td>
                  <ActionsMenu
                    actions={[
                      {
                        label: 'View',
                        onClick: () =>
                          history.push(`/nutritional-panel-uploads/${nutritional_panel.id}`),
                      },
                      {
                        label: 'View OCR Key',
                        onClick: () => setSelectedOcrKey(nutritional_panel.ocr_key),
                        hidden: !nutritional_panel.ocr_key,
                      },
                      {
                        label: nutritional_panel.ocr_processed_file_url
                          ? 'Download OCR File'
                          : 'OCR File Not Available',
                        onClick: () =>
                          (window.location.href = nutritional_panel.ocr_processed_file_url),
                        disabled: !nutritional_panel.ocr_processed_file_url,
                      },
                      {
                        label: 'Re-process',
                        onClick: () => handleReprocess(nutritional_panel.id),
                        disabled: reprocessLoading,
                        hidden:
                          nutritional_panel.status !== ProcessStatus.PROCESSING &&
                          nutritional_panel.ocr_status !== OCRStatus.PROCESSING,
                      },
                    ]}
                  />
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="100%" className="text-center">
                No nutritional panel uploads found.
              </td>
            </tr>
          )}
        </tbody>
      </Table>
      {showPanelUploadModal ? (
        <NutritionalPanelUploadModal
          onHide={() => setShowPanelUploadModal(false)}
          refetch={refetch}
        />
      ) : null}
      <CopyTextModal
        show={selectedOcrKey !== null}
        onHide={() => setSelectedOcrKey(null)}
        title="OCR Key"
        text={selectedOcrKey}
      />
    </Container>
  );
}

const styles = StyleSheet.create({
  tableText: {
    fontSize: '0.95rem',
  },
  thead: {
    position: 'sticky-top',
    zIndex: 1,
    backgroundColor: backgroundGrey,
  },
  tbody: {
    zIndex: 0,
    position: 'relative',
  },
  nameCell: {
    maxWidth: '180px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
});

export default NutritionalPanelUploadView;
